import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DollarSign,
  MapPin,
  CreditCard,
  TrendingUp,
  Loader2,
  ChevronRight,
  Globe,
  Flag,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import api from '@/lib/api'
import type {
  BaselineProjectionResponse,
  ScenarioSuggestion,
  AppliedFactor,
  ProjectionResult,
} from '@/types'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
)

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington DC' }
]

interface ScenarioOnboardingProps {
  onComplete: (settings: {
    yearly_income: number
    state: string
    monthly_expense: number
    monthly_income_after_tax: number
    applied_factors?: AppliedFactor[]
  }) => void
  onClose?: () => void
  suggestedExpense?: number
  viewMode?: 'personal' | 'household'
}

export function ScenarioOnboarding({ onComplete, onClose, suggestedExpense = 5000, viewMode = 'personal' }: ScenarioOnboardingProps) {
  const [step, setStep] = useState(0)
  const [isInUS, setIsInUS] = useState<boolean | null>(null)
  const [yearlyIncome, setYearlyIncome] = useState<number>(150000)
  const [state, setState] = useState<string>('CA')
  const [monthlyExpense, setMonthlyExpense] = useState<number>(suggestedExpense)
  const [taxInfo, setTaxInfo] = useState<{
    monthly_income_after_tax: number
    effective_tax_rate: number
    federal_tax: number
    state_tax: number
  } | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Baseline step state
  const [baselineData, setBaselineData] = useState<BaselineProjectionResponse | null>(null)
  const [isLoadingBaseline, setIsLoadingBaseline] = useState(false)
  const [suggestions, setSuggestions] = useState<ScenarioSuggestion[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const [appliedFactors, setAppliedFactors] = useState<AppliedFactor[]>([])
  const [scenarioResult, setScenarioResult] = useState<ProjectionResult | null>(null)
  const [isRunningScenario, setIsRunningScenario] = useState(false)
  const [showAssumptions, setShowAssumptions] = useState(false)

  // Calculate tax when income or state changes (US only)
  useEffect(() => {
    if (isInUS && step === 2 && yearlyIncome > 0) {
      const timer = setTimeout(async () => {
        setIsCalculating(true)
        try {
          const response = await api.post('/scenarios/estimate-tax', {
            yearly_income: yearlyIncome,
            state: state
          })
          setTaxInfo(response.data)
        } catch (err) {
          console.error('Failed to estimate tax:', err)
        } finally {
          setIsCalculating(false)
        }
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [yearlyIncome, state, step, isInUS])

  // For international users, set taxInfo based on after-tax income directly
  useEffect(() => {
    if (!isInUS && yearlyIncome > 0) {
      setTaxInfo({
        monthly_income_after_tax: yearlyIncome / 12,
        effective_tax_rate: 0,
        federal_tax: 0,
        state_tax: 0
      })
    }
  }, [yearlyIncome, isInUS])

  // Determine if we're on the baseline step
  const isBaselineStep = (isInUS === true && step === 5) || (isInUS === false && step === 4)

  // Fetch baseline and suggestions when entering baseline step
  useEffect(() => {
    if (isBaselineStep && !baselineData && !isLoadingBaseline) {
      const fetchData = async () => {
        setIsLoadingBaseline(true)
        setIsLoadingSuggestions(true)

        try {
          const [baselineResponse, suggestionsResponse] = await Promise.all([
            api.post('/scenarios/baseline-projection', {
              yearly_income: yearlyIncome,
              state: isInUS ? state : 'INTL',
              monthly_expense: monthlyExpense,
              is_after_tax: !isInUS,
              view: viewMode,
              time_horizon_months: 24
            }),
            api.get('/scenarios/suggestions', { params: { view: viewMode } })
          ])

          setBaselineData(baselineResponse.data)
          setSuggestions(suggestionsResponse.data.suggestions || [])
        } catch (err) {
          console.error('Failed to fetch baseline/suggestions:', err)
        } finally {
          setIsLoadingBaseline(false)
          setIsLoadingSuggestions(false)
        }
      }

      fetchData()
    }
  }, [isBaselineStep, baselineData, isLoadingBaseline, yearlyIncome, state, monthlyExpense, isInUS, viewMode])

  // Run projection when factors change
  const runScenarioProjection = useCallback(async (factors: AppliedFactor[]) => {
    if (factors.length === 0) {
      setScenarioResult(null)
      return
    }

    setIsRunningScenario(true)
    try {
      const allEffects = factors.flatMap(f => f.effects)
      const response = await api.post('/scenarios/run-adhoc', {
        spec: {
          title: 'Combined Scenario',
          time_horizon_months: 24,
          assumptions: [],
          effects: allEffects,
          needs_clarification: false,
          clarifying_questions: []
        },
        view: viewMode,
        yearly_income: yearlyIncome,
        state: isInUS ? state : 'INTL',
        monthly_expense: monthlyExpense
      })
      setScenarioResult(response.data.result)
    } catch (err) {
      console.error('Failed to run scenario:', err)
    } finally {
      setIsRunningScenario(false)
    }
  }, [viewMode, yearlyIncome, state, monthlyExpense, isInUS])

  const handleAddFactor = async (suggestion: ScenarioSuggestion) => {
    // Check if already added
    if (appliedFactors.some(f => f.id === suggestion.id)) return

    const newFactor: AppliedFactor = {
      id: suggestion.id,
      title: suggestion.title,
      effects: suggestion.spec.effects
    }

    const newFactors = [...appliedFactors, newFactor]
    setAppliedFactors(newFactors)
    await runScenarioProjection(newFactors)
  }

  const handleRemoveFactor = async (factorId: string) => {
    const newFactors = appliedFactors.filter(f => f.id !== factorId)
    setAppliedFactors(newFactors)
    await runScenarioProjection(newFactors)
  }

  const handleComplete = async () => {
    setIsSaving(true)
    try {
      const response = await api.post('/scenarios/settings', {
        yearly_income: yearlyIncome,
        state: isInUS ? state : 'INTL',
        monthly_expense: monthlyExpense,
        is_after_tax: !isInUS
      })
      onComplete({
        yearly_income: yearlyIncome,
        state: isInUS ? state : 'INTL',
        monthly_expense: monthlyExpense,
        monthly_income_after_tax: response.data.settings.monthly_income_after_tax,
        applied_factors: appliedFactors.length > 0 ? appliedFactors : undefined
      })
    } catch (err) {
      console.error('Failed to save settings:', err)
    } finally {
      setIsSaving(false)
    }
  }

  // Dynamic steps based on location
  const getSteps = () => {
    const baseSteps = [
      {
        title: "Let's set up your financial baseline",
        subtitle: "This helps us project your future net worth accurately",
        icon: TrendingUp
      },
      {
        title: "Where are you located?",
        subtitle: "This determines how we calculate your taxes",
        icon: Globe
      }
    ]

    if (isInUS === true) {
      return [
        ...baseSteps,
        {
          title: "What's your annual income?",
          subtitle: "Enter your pre-tax salary - we'll calculate taxes for you",
          icon: DollarSign
        },
        {
          title: "Which state?",
          subtitle: "State taxes vary significantly",
          icon: MapPin
        },
        {
          title: "Monthly expenses?",
          subtitle: "Your average monthly spending",
          icon: CreditCard
        },
        {
          title: "Your Financial Baseline",
          subtitle: "Add scenarios to see their impact",
          icon: Sparkles
        }
      ]
    } else if (isInUS === false) {
      return [
        ...baseSteps,
        {
          title: "What's your annual income?",
          subtitle: "Enter your after-tax (take-home) income",
          icon: DollarSign
        },
        {
          title: "Monthly expenses?",
          subtitle: "Your average monthly spending",
          icon: CreditCard
        },
        {
          title: "Your Financial Baseline",
          subtitle: "Add scenarios to see their impact",
          icon: Sparkles
        }
      ]
    }

    return baseSteps
  }

  const steps = getSteps()
  const currentStep = steps[step]
  const Icon = currentStep?.icon || TrendingUp

  const monthlyIncomeAfterTax = isInUS ? (taxInfo?.monthly_income_after_tax || 0) : yearlyIncome / 12
  const monthlySavings = monthlyIncomeAfterTax - monthlyExpense
  const savingsRate = monthlyIncomeAfterTax > 0 ? (monthlySavings / monthlyIncomeAfterTax) * 100 : 0

  const canProceed = () => {
    if (step === 1) return isInUS !== null
    if (step === 2) return yearlyIncome > 0
    if (isInUS && step === 3) return state !== ''
    return true
  }

  // Chart data for baseline step
  const chartData = baselineData ? {
    labels: baselineData.projection.baseline_series.map(p => p.month === 0 ? 'Now' : `${p.month}mo`),
    datasets: [
      {
        label: 'Baseline',
        data: baselineData.projection.baseline_series.map(p => p.value),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      ...(scenarioResult ? [{
        label: 'With Scenarios',
        data: scenarioResult.scenario_series.map(p => p.value),
        borderColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderDash: [5, 5],
      }] : [])
    ]
  } : null

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: appliedFactors.length > 0,
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: { dataset: { label?: string }, parsed: { y: number | null } }) => {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y ?? 0)}`
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 6,
          font: { size: 10 }
        }
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: {
          callback: (value: number | string) => {
            const numValue = typeof value === 'string' ? parseFloat(value) : value
            if (numValue >= 1000000) return `$${(numValue / 1000000).toFixed(1)}M`
            if (numValue >= 1000) return `$${(numValue / 1000).toFixed(0)}K`
            return `$${numValue}`
          },
          font: { size: 10 }
        }
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={`relative w-full mx-4 overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl ${
          isBaselineStep ? 'max-w-2xl' : 'max-w-lg'
        }`}
      >
        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-800">
          <motion.div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            initial={{ width: 0 }}
            animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500"
                >
                  <Icon className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {currentStep?.title}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {currentStep?.subtitle}
                  </p>
                </div>
              </div>

              {/* Step content */}
              {step === 0 && (
                <div className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-6"
                  >
                    <p className="text-gray-700 dark:text-gray-300">
                      To show you accurate "what-if" scenarios, we need to know:
                    </p>
                    <ul className="mt-4 space-y-3">
                      {[
                        'Where you live',
                        'Your annual income',
                        'Your monthly expenses'
                      ].map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + i * 0.1 }}
                          className="flex items-center gap-3 text-gray-600 dark:text-gray-400"
                        >
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50 text-xs font-semibold text-purple-600 dark:text-purple-400">
                            {i + 1}
                          </span>
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <motion.button
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      onClick={() => setIsInUS(true)}
                      className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
                        isInUS === true
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <Flag className={`h-8 w-8 ${isInUS === true ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className={`font-semibold ${isInUS === true ? 'text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'}`}>
                        United States
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        We'll calculate taxes
                      </span>
                    </motion.button>

                    <motion.button
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      onClick={() => setIsInUS(false)}
                      className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
                        isInUS === false
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <Globe className={`h-8 w-8 ${isInUS === false ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className={`font-semibold ${isInUS === false ? 'text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'}`}>
                        Other Country
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Enter after-tax income
                      </span>
                    </motion.button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">$</span>
                    <Input
                      type="number"
                      value={yearlyIncome || ''}
                      onChange={(e) => setYearlyIncome(Number(e.target.value) || 0)}
                      placeholder="150,000"
                      className="h-14 pl-10 text-2xl font-semibold"
                      autoFocus
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">/year</span>
                  </div>

                  {isInUS && taxInfo && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="rounded-xl bg-gray-50 dark:bg-gray-800 p-4 space-y-2"
                    >
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Federal Tax</span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatCurrency(taxInfo.federal_tax)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">State Tax ({state})</span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatCurrency(taxInfo.state_tax)}
                        </span>
                      </div>
                      <div className="border-t dark:border-gray-700 pt-2 flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          After-Tax Monthly
                        </span>
                        <span className="font-bold text-green-600">
                          {formatCurrency(taxInfo.monthly_income_after_tax)}/mo
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 text-center">
                        Effective tax rate: {taxInfo.effective_tax_rate}%
                      </div>
                    </motion.div>
                  )}

                  {!isInUS && yearlyIncome > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="rounded-xl bg-green-50 dark:bg-green-900/20 p-4"
                    >
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Monthly Take-Home
                        </span>
                        <span className="font-bold text-green-600">
                          {formatCurrency(yearlyIncome / 12)}/mo
                        </span>
                      </div>
                    </motion.div>
                  )}

                  {isCalculating && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                      <span className="ml-2 text-sm text-gray-500">Calculating taxes...</span>
                    </div>
                  )}
                </div>
              )}

              {isInUS && step === 3 && (
                <div className="space-y-6">
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger className="h-14 text-lg">
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {US_STATES.map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {state && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-4"
                    >
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {['FL', 'TX', 'WA', 'NV', 'WY', 'AK', 'SD', 'TN', 'NH'].includes(state)
                          ? `Great choice! ${US_STATES.find(s => s.code === state)?.name} has no state income tax.`
                          : `${US_STATES.find(s => s.code === state)?.name} will be factored into your tax calculation.`
                        }
                      </p>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Expenses step - different index based on location */}
              {((isInUS && step === 4) || (!isInUS && step === 3)) && (
                <div className="space-y-6">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">$</span>
                    <Input
                      type="number"
                      value={monthlyExpense || ''}
                      onChange={(e) => setMonthlyExpense(Number(e.target.value) || 0)}
                      placeholder="5,000"
                      className="h-14 pl-10 text-2xl font-semibold"
                      autoFocus
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">/month</span>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Your Monthly Summary
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {isInUS ? 'After-Tax Income' : 'Take-Home Income'}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(monthlyIncomeAfterTax)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Expenses</span>
                        <span className="font-medium text-red-600">
                          -{formatCurrency(monthlyExpense)}
                        </span>
                      </div>
                      <div className="border-t dark:border-gray-700 pt-3 flex justify-between">
                        <span className="font-semibold text-gray-900 dark:text-white">Monthly Savings</span>
                        <span className={`font-bold text-xl ${monthlySavings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(monthlySavings)}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className={`text-sm font-medium ${savingsRate >= 20 ? 'text-green-600' : savingsRate >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {savingsRate.toFixed(0)}% savings rate
                          {savingsRate >= 20 && ' - Excellent!'}
                          {savingsRate >= 10 && savingsRate < 20 && ' - Good'}
                          {savingsRate < 10 && savingsRate >= 0 && ' - Could improve'}
                          {savingsRate < 0 && ' - Spending more than earning'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Baseline step */}
              {isBaselineStep && (
                <div className="space-y-5">
                  {isLoadingBaseline ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                      <p className="mt-4 text-sm text-gray-500">Calculating your projection...</p>
                    </div>
                  ) : baselineData ? (
                    <>
                      {/* Summary stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-center">
                          <p className="text-xs text-gray-500">Starting Net Worth</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatCurrency(baselineData.assumptions.starting_net_worth)}
                          </p>
                        </div>
                        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-center">
                          <p className="text-xs text-gray-500">Monthly Savings</p>
                          <p className={`text-lg font-bold ${baselineData.assumptions.monthly_savings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(baselineData.assumptions.monthly_savings)}
                          </p>
                        </div>
                        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-center">
                          <p className="text-xs text-gray-500">Est. Growth Rate</p>
                          <p className="text-lg font-bold text-blue-600">
                            ~{baselineData.assumptions.weighted_annual_return}%/yr
                          </p>
                        </div>
                      </div>

                      {/* Chart */}
                      <div className="h-44">
                        {chartData && (
                          <Line data={chartData} options={chartOptions} />
                        )}
                      </div>

                      {/* Projection result */}
                      <div className="rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">In 24 months (Baseline)</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                              {formatCurrency(baselineData.projection.final_baseline)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Growth</p>
                            <p className="text-lg font-semibold text-green-600">
                              +{formatCurrency(baselineData.projection.final_baseline - baselineData.assumptions.starting_net_worth)}
                            </p>
                          </div>
                        </div>
                        {scenarioResult && (
                          <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-800 flex items-center justify-between">
                            <div>
                              <p className="text-sm text-orange-600 dark:text-orange-400">With Scenarios</p>
                              <p className="text-xl font-bold text-orange-600">
                                {formatCurrency(scenarioResult.final_scenario)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600 dark:text-gray-400">Impact</p>
                              <p className={`text-lg font-semibold ${scenarioResult.impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {scenarioResult.impact >= 0 ? '+' : ''}{formatCurrency(scenarioResult.impact)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Collapsible assumptions */}
                      <button
                        onClick={() => setShowAssumptions(!showAssumptions)}
                        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {showAssumptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        How is this calculated?
                      </button>
                      {showAssumptions && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 text-sm text-gray-600 dark:text-gray-400"
                        >
                          <p className="mb-2">Monthly savings added proportionally to your current allocation:</p>
                          <ul className="space-y-1 ml-4">
                            {Object.entries(baselineData.assumptions.return_assumptions).map(([asset, ret]) => (
                              <li key={asset}>• {asset}: {ret} annual return</li>
                            ))}
                          </ul>
                        </motion.div>
                      )}

                      {/* Applied factors */}
                      {appliedFactors.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Applied scenarios:</p>
                          <div className="flex flex-wrap gap-2">
                            {appliedFactors.map((factor) => (
                              <span
                                key={factor.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-sm"
                              >
                                {factor.title}
                                <button
                                  onClick={() => handleRemoveFactor(factor.id)}
                                  className="ml-1 hover:text-orange-900 dark:hover:text-orange-100"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Suggestions */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {appliedFactors.length > 0 ? 'Add more scenarios:' : 'Explore "what-if" scenarios:'}
                        </p>
                        {isLoadingSuggestions ? (
                          <div className="flex items-center gap-2 py-2">
                            <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                            <span className="text-sm text-gray-500">Loading suggestions...</span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {suggestions.slice(0, 5).map((suggestion) => {
                              const isApplied = appliedFactors.some(f => f.id === suggestion.id)
                              return (
                                <Button
                                  key={suggestion.id}
                                  variant="outline"
                                  size="sm"
                                  className={`text-xs ${
                                    isApplied
                                      ? 'bg-orange-50 border-orange-300 text-orange-700 dark:bg-orange-900/20 dark:border-orange-700 dark:text-orange-300'
                                      : ''
                                  }`}
                                  onClick={() => handleAddFactor(suggestion)}
                                  disabled={isApplied || isRunningScenario}
                                >
                                  {isRunningScenario && !isApplied ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  ) : null}
                                  {suggestion.title}
                                </Button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-red-500 py-8">
                      Failed to load projection. Click Continue to proceed anyway.
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            {step > 0 ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setStep(step - 1)
                  // Reset baseline data when going back from baseline step
                  if (isBaselineStep) {
                    setBaselineData(null)
                    setAppliedFactors([])
                    setScenarioResult(null)
                  }
                }}
              >
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < steps.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                Continue
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={isSaving || monthlyIncomeAfterTax <= 0}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Done
                    <TrendingUp className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}
